<template>
  <div class="w-full">
    <div class="bg-white shadow-lg rounded-2xl border p-6">
      <!-- HEADER -->
      <div class="flex justify-between items-center mb-4">
        <div>
          <p class="text-xs font-bold text-indigo-500 uppercase">
            Learner Analytics
          </p>
          <h2 class="text-xl font-bold">Learner  by Month</h2>
        </div>
      </div>
      <div
        class="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center mb-4"
      >
        <!-- Left Side - Filters Section -->
        <div
          class="flex flex-col md:flex-row flex-wrap gap-4 items-start md:items-center flex-1"
        >
          <!-- Quick Filters -->
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="option in filters"
              :key="option.value"
              @click="selectFilter(option)"
              :class="[
                'px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 border',
                selectedFilter.value === option.value
                  ? 'bg-blues text-white border-blue-600 shadow-md'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400',
              ]"
            >
              {{ option.label }}
            </button>
          </div>

          <!-- Custom Date Picker -->
          <div
            v-if="selectedFilter.value === 'custom'"
            class="flex gap-3 items-center flex-wrap p-3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm"
          >
            <div class="flex flex-col relative">
              <label
                class="absolute -top-2.5 left-3 bg-gray-50 px-1 text-xs font-medium text-gray-600"
              >
                Start Date
              </label>
              <input
                type="date"
                v-model="customStart"
                class="px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            <span class="text-gray-500 font-medium">→</span>

            <div class="flex flex-col relative">
              <label
                class="absolute -top-2.5 left-3 bg-gray-50 px-1 text-xs font-medium text-gray-600"
              >
                End Date
              </label>
              <input
                type="date"
                v-model="customEnd"
                class="px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            <button
              @click="applyCustomDate"
              class="px-5 py-2.5 bg-blues hover:bg-hover text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Apply
            </button>
          </div>

          <!-- Cancel Button - Icon Only -->
          <button
            v-if="selectedFilter.value"
            @click="cancelAllFilters"
            class="p-2.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-all duration-200 group hover:shadow-sm"
            aria-label="Cancel filters"
            title="Clear all filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 transition-transform duration-200 group-hover:rotate-90"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <a
          :href="downloads.download_url"
          class="flex cursor-pointer items-center gap-2.5 px-6 py-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg text-sm font-medium text-gray-700 shadow-lg hover:shadow-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 group whitespace-nowrap"
        >
          <div
            class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-1 group-hover:scale-110 group-hover:from-blue-100 group-hover:to-indigo-100 transition-all duration-300"
          >
            <svg
              class="w-4 h-4 text-blue-600 group-hover:text-blue-700 transition-colors duration-300"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <!-- Download arrow with document -->
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 3v12m0 0l-3-3m3 3l3-3M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2"
              />
            </svg>
          </div>
          <span>Export Data</span>
        </a>
      </div>

      <!-- ✅ MONTHLY TILES -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <!-- STUDENT -->
        <div class="bg-blue-50 p-4 rounded-xl">
          <p class="text-sm text-gray-600">Leaner</p>
          <p class="text-xl font-bold text-blue-600">
            {{ overallData.student }}
          </p>
          <p class="text-xs text-blue-500">
            {{ overallData.student_percentage }}%
          </p>
        </div>

        <!-- INSTRUCTOR -->
        <div class="bg-green-50 p-4 rounded-xl">
          <p class="text-sm text-gray-600">Instructors</p>
          <p class="text-xl font-bold text-green-600">
            {{ overallData.instructor }}
          </p>
          <p class="text-xs text-green-500">
            {{ overallData.instructor_percentage }}%
          </p>
        </div>

        <!-- HOD -->
        <!-- <div class="bg-yellow-50 p-4 rounded-xl">
          <p class="text-sm text-gray-600">HOD</p>
          <p class="text-xl font-bold text-yellow-600">
            {{ overallData.hod }}
          </p>
          <p class="text-xs text-yellow-500">
            {{ overallData.hod_percentage }}%
          </p>
        </div> -->

        <!-- COORDINATOR -->
        <!-- <div class="bg-red-50 p-4 rounded-xl">
          <p class="text-sm text-gray-600">Coordinator</p>
          <p class="text-xl font-bold text-red-600">
            {{ overallData.coordinator }}
          </p>
          <p class="text-xs text-red-500">
            {{ overallData.coordinator_percentage }}%
          </p>
        </div> -->
      </div>
      <div class="mt-4 mb-2">
        <h3 class="text-lg font-semibold text-slate-800">
          Monthly Learner  Trends
        </h3>
        <p class="text-sm text-gray-500">
          Hover on any data point to see individual Learner  count
        </p>
      </div>
      <!-- ✅ CHART -->
      <apexchart
        type="area"
        height="350"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  components: { apexchart: VueApexCharts },

  data() {
    return {
      lead_counts: {},

      months: [],
      studentValues: [],
      instructorValues: [],
      hodValues: [],
      coOrdinateValues: [],

      series: [],
      chartOptions: {},

      // ✅ selected month data
      selectedMonthIndex: null,
      // selectedMonthData: {
      //   student: 0,
      //   instructor: 0,
      //   hod: 0,
      //   coordinator: 0,
      // },
      overallData: {
        student : 0,
        instructor: 0,
        hod: 0,
        coordinator: 0,
        student_percentage: 0,
        instructor_percentage: 0,
        hod_percentage: 0,
        coordinator_percentage: 0,
      },
      filters: [
        { label: "1 Day", value: "1" },
        { label: "7 Days", value: "7" },
        { label: "30 Days", value: "30" },
        { label: "Custom", value: "custom" },
      ],
      selectedFilter: {},
      customStart: "",
      customEnd: "",
      downloads: "",
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),
  },

  mounted() {
    this.getLeads();
  },
  created() {
    this.getDownload();
  },

  methods: {
    selectFilter(option) {
      this.selectedFilter = option;

      if (option.value !== "custom") {
        this.applyQuickFilter(option.value);
      }
    },

    applyQuickFilter(days) {
      const end = new Date();
      const start = new Date();

      start.setDate(end.getDate() - parseInt(days));

      const format = (d) => d.toISOString().split("T")[0];

      this.fetchData(format(start), format(end));
    },

    applyCustomDate() {
      if (!this.customStart || !this.customEnd) {
        alert("Please select both dates");
        return;
      }

      this.fetchData(this.customStart, this.customEnd);
        this.getDownload(this.customStart, this.customEnd);
    },
    cancelAllFilters() {
      this.selectedFilter = {};
      this.customStart = "";
      this.customEnd = "";

      this.getLeads();
    },
    async fetchData(start = null, end = null) {
      if (!start || !end) {
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/total-leads/?start_date=${start}&end_date=${end}&Key=${this.userInfo.token_key}`,
        );

        if (res.data.status === 200) {
          const data = res.data.data;

          this.overallData = {
            student: data.student_count || 0,
            instructor: data.instructor_count || 0,
            hod: data.hod_count || 0,
            coordinator: data.coordinator_count || 0,
            student_percentage: data.student_percentage || 0,
            instructor_percentage: data.instructor_percentage || 0,
            hod_percentage: data.hod_percentage || 0,
            coordinator_percentage: data.coordinator_percentage || 0,
          };

          const monthly = data.monthly_role_counts;

          const student = monthly.student || [];
          const instructor = monthly.instructor || [];
          const hod = monthly.hod || [];
          const coordinator = monthly["co-ordinator"] || [];

          // ✅ COLLECT UNIQUE MONTHS
          const allMonths = new Set([
            ...student.map((i) => i.month),
            ...instructor.map((i) => i.month),
            ...hod.map((i) => i.month),
            ...coordinator.map((i) => i.month),
          ]);

          this.months = Array.from(allMonths).sort(
            (a, b) => new Date(a) - new Date(b),
          );

          // ✅ MAP DATA TO MONTHS
          const mapData = (arr) => {
            const map = Object.fromEntries(arr.map((i) => [i.month, i.count]));
            return this.months.map((m) => map[m] || 0);
          };

          this.studentValues = mapData(student);
          this.instructorValues = mapData(instructor);
          this.hodValues = mapData(hod);
          this.coOrdinateValues = mapData(coordinator);

          this.setupChart();
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getLeads() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/total-leads/?Key=${this.userInfo.token_key}`,
        );

        if (res.data.status === 200) {
          const data = res.data.data;

          // ✅ SET OVERALL DATA
          this.overallData = {
            student: data.student_count || 0,
            instructor: data.instructor_count || 0,
            hod: data.hod_count || 0,
            coordinator: data.coordinator_count || 0,
            student_percentage: data.student_percentage || 0,
            instructor_percentage: data.instructor_percentage || 0,
            hod_percentage: data.hod_percentage || 0,
            coordinator_percentage: data.coordinator_percentage || 0,
          };

          const monthly = data.monthly_role_counts;

          const student = monthly.student || [];
          const instructor = monthly.instructor || [];
          const hod = monthly.hod || [];
          const coordinator = monthly["co-ordinator"] || [];

          // ✅ COLLECT UNIQUE MONTHS
          const allMonths = new Set([
            ...student.map((i) => i.month),
            ...instructor.map((i) => i.month),
            ...hod.map((i) => i.month),
            ...coordinator.map((i) => i.month),
          ]);

          this.months = Array.from(allMonths).sort(
            (a, b) => new Date(a) - new Date(b),
          );

          // ✅ MAP DATA TO MONTHS
          const mapData = (arr) => {
            const map = Object.fromEntries(arr.map((i) => [i.month, i.count]));
            return this.months.map((m) => map[m] || 0);
          };

          this.studentValues = mapData(student);
          this.instructorValues = mapData(instructor);
          this.hodValues = mapData(hod);
          this.coOrdinateValues = mapData(coordinator);

          this.setupChart();
        }
      } catch (err) {
        console.error(err);
      }
    },
   
   getDownload(startDate, endDate) {
  let url = `${process.env.VUE_APP_API}/current-month-student-export/`;
  let params = {
    Key: this.userInfo.token_key,
  };

  // Add custom start and end dates to the export request
  if (startDate && endDate) {
    params.start_date = startDate;
    params.end_date = endDate;
  }

  axios
    .get(url, { params })
    .then((response) => {
      if (response.data.status === 200) {
        this.downloads = response.data;
      } else {
        console.log("Something went wrong with the download.");
      }
    })
    .catch((error) => {
      console.log("Error:", error);
    });
},
    setupChart() {
      const self = this;

      this.series = [
        { name: "Learner", data: this.studentValues },
        { name: "Instructor", data: this.instructorValues },
        // { name: "HOD", data: this.hodValues },
        // { name: "Coordinator", data: this.coOrdinateValues },
      ];

      this.chartOptions = {
        chart: {
          type: "area",
          toolbar: { show: false },
          zoom: { enabled: false },
        },

        colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"],

        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: 0.4,
            opacityTo: 0.1,
          },
        },

        stroke: {
          curve: "smooth",
          width: 2,
        },

        xaxis: {
          categories: this.months,
        },

        dataLabels: {
          enabled: false,
        },

        tooltip: {
          shared: true,
          intersect: false,
          followCursor: false,

          custom: function ({ series, dataPointIndex }) {
            const category = self.months[dataPointIndex];

            const student = series[0][dataPointIndex] || 0;
            const instructor = series[1][dataPointIndex] || 0;
            const hod = series[2][dataPointIndex] || 0;
            const coordinator = series[3][dataPointIndex] || 0;

            // ✅ Sync tiles (clean logic)
            self.selectedMonthIndex = dataPointIndex;
            self.selectedMonthLabel = category;
            self.selectedMonthData = {
              student,
              instructor,
              hod,
              coordinator,
            };

            const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];
            const names = ["Student", "Instructor", "HOD", "Coordinator"];
            const values = [student, instructor, hod, coordinator];

            const rows = names
              .map(
                (name, i) => `
      <div style="
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:20px;
        padding:5px 0;
        border-bottom:${i < 3 ? "1px solid #f3f4f6" : "none"};
      ">
        <span style="
          display:flex;
          align-items:center;
          gap:7px;
          font-size:12px;
          color:#6b7280;
        ">
          <span style="
            width:8px;
            height:8px;
            border-radius:50%;
            background:${colors[i]};
          "></span>
          ${name}
        </span>

        <span style="
          font-size:13px;
          font-weight:600;
          color:${colors[i]};
        ">
          ${values[i]}
        </span>
      </div>
    `,
              )
              .join("");

            return `
      <div style="
        background:#fff;
        border:1px solid #e5e7eb;
        border-radius:12px;
        padding:12px 16px;
        min-width:180px;
        box-shadow:0 4px 16px rgba(0,0,0,0.08);
      ">
        <div style="
          font-size:12px;
          font-weight:600;
          color:#111827;
          margin-bottom:8px;
          padding-bottom:6px;
          border-bottom:1px solid #f3f4f6;
        ">
          ${category}
        </div>

        ${rows}
      </div>
    `;
          },
        },
      };

      // ✅ default last month
      this.$nextTick(() => {
        const last = this.months.length - 1;

        this.selectedMonthIndex = last;
        this.selectedMonthData = {
          student: this.studentValues[last] || 0,
          instructor: this.instructorValues[last] || 0,
          hod: this.hodValues[last] || 0,
          coordinator: this.coOrdinateValues[last] || 0,
        };
      });
    },
  },
};
</script>
